/**
 * Entering availability for an instructor — SPEC §9.3, §9.4.
 *
 * The fallback that makes the rollout safe. Until everyone is on the portal somebody will
 * text Luke their days, and without this there is nowhere to put them: the alternative is a
 * note in a spreadsheet that the coverage grid cannot see, which is the exact problem this
 * whole system exists to end.
 *
 * ## It is the instructor's own screen
 *
 * Not a similar screen — the same component, with a different API adapter and a change of
 * voice. Those adapters reach the same server handlers by two paths (see
 * `routes/availability.ts`), so every rule the instructor's screen enforces applies here:
 * location membership, past dates, the horizon, one slot per period.
 *
 * A separate "admin entry form" would have been quicker and would have drifted within a
 * month. The first thing to drift would be the exception encoding, and the symptom would be
 * availability that reads fine on this screen and produces different rows than the
 * instructor's own — with nothing to say which was right.
 *
 * ## Every entry is attributed
 *
 * `submitMonth` records `enteredByAdminEmail`, so "who entered this on her behalf?" has an
 * answer. That matters most when it turns out to be wrong: the instructor said the 14th and
 * somebody typed the 4th, and the record has to show that the instructor never said it.
 */

import { useMemo, useState } from 'react';
import { onBehalfApi, rosterApi, type RosterInstructorRow } from '../../lib/api.js';
import { Availability } from '../Availability.js';

export function OnBehalf({
  instructor,
  onDone,
}: {
  instructor: RosterInstructorRow;
  onDone: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);

  const api = useMemo(() => onBehalfApi(instructor.id), [instructor.id]);
  const voice = useMemo(
    () => ({ subjectName: instructor.firstName, possessive: 'their', subject: 'they' }),
    [instructor.firstName],
  );

  /**
   * A deliberate stop before the first keystroke.
   *
   * This screen writes somebody else's availability under their name, and the failure it
   * guards against is mundane: the wrong row was clicked in a list of thirty, and Luke
   * enters Cassius's days onto Carissa's month. Nothing on the editor itself would look
   * wrong, and the mistake would surface as a class nobody turns up to.
   */
  if (!confirmed) {
    return (
      <div className="card stack">
        <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>
          Enter availability for {instructor.name}
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          This records days under <strong>{instructor.name}</strong>’s name, exactly as if
          they had entered them. Your name is stored against it, and they can change it
          themselves afterwards.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          {instructor.email}
          {instructor.homeLocationName && ` · ${instructor.homeLocationName}`}
          {instructor.patternSlots > 0
            ? ` · ${instructor.patternSlots} standing class${instructor.patternSlots === 1 ? '' : 'es'}`
            : ' · no usual schedule'}
        </p>
        <div className="row">
          <button className="btn btn--primary" onClick={() => setConfirmed(true)}>
            Yes, this is the right person
          </button>
          <button className="btn btn--secondary" onClick={onDone}>
            Back to the roster
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>{instructor.name}</h2>
          {/* Kept on screen for the whole session, not just at the start. The confirmation
              above is easy to click past, and this is the line that catches it. */}
          <span className="small muted">
            You are entering availability on their behalf · {instructor.email}
          </span>
        </div>
        <button className="btn btn--secondary" onClick={onDone}>
          Back to the roster
        </button>
      </div>

      {/* Deliberately does not navigate away on submit: the confirmation says what was
          recorded, and whisking the screen away is how somebody ends up unsure whether it
          saved. "Back to the roster" is always there. */}
      <Availability
        availability={api}
        voice={voice}
        // THEIR breaks, read through the admin endpoint. The default reads the signed-in
        // instructor's own, which on this screen is whoever the admin happens to also be
        // signed in as — a different person wearing this one's name.
        leaves={() => rosterApi.leave.list(instructor.id)}
      />
    </div>
  );
}
